"use client";

import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import { useEffect, useRef } from "react";
import { Address } from "viem";

import {
  defaultAccountState,
  defaultAccountStateTimestamps,
} from "#/config/accounts";
import { ONE_SECOND_MS } from "#/config/time";
import { useFetchAccountState } from "#/services/kyan/hooks/useFetchAccountState";
import { useServerAccount } from "#/services/kyan/hooks/useServerAccount";
import { setServerAccount } from "#/services/kyan/kyan.service";
import {
  Pairs,
  PortfolioSymbol,
  type PortfolioSymbol as PortfolioSymbolType,
} from "#/types/markets";

import { useMirrorAccount } from "../app/devMode/mirrorAccount";
import { useAccount } from "./account/account";
import { useAccountState } from "./account/accountState";
import { useAccountStateTimestamps } from "./account/accountStateTimestamps";
import { usePositions } from "./account/positions";
import { useStalePortfolios } from "./account/stalePortfolios";
import { useEoa } from "./eoa/eoa";

const IDLE_THRESHOLD = ONE_SECOND_MS * 15;

export const AccountUpdater = () => {
  const { setEoa } = useEoa();
  const { setPositions } = usePositions();
  const { account, setAccount } = useAccount();
  const { mirrorAccount } = useMirrorAccount();
  const { setAccountState } = useAccountState();
  const { primaryWallet } = useDynamicContext();
  const { accountStateTimestamps, setAccountStateTimestamps } =
    useAccountStateTimestamps();
  const { serverAccount, refetch: refetchServerAccount } = useServerAccount();
  const { stalePortfolios, shouldFetchAccountState, setStalePortfolios } =
    useStalePortfolios();
  const { setMirrorAccount } = useMirrorAccount();

  const accountStateTimestampsRef = useRef(accountStateTimestamps);

  useEffect(() => {
    accountStateTimestampsRef.current = accountStateTimestamps;
  }, [accountStateTimestamps]);

  const { positions, accountState } = useFetchAccountState({
    options: {
      enabled: shouldFetchAccountState,
      refetchInterval: shouldFetchAccountState ? 10_000 : undefined,
    },
  });

  // Check for stale timestamps every 10 seconds
  useEffect(() => {
    if (!account) return;

    const interval = setInterval(() => {
      const now = Date.now();

      setStalePortfolios((previous) =>
        Object.entries(accountStateTimestampsRef.current).reduce(
          (updatedPortfolios, [portfolio, timestamp]) => {
            const timeDiff = now - timestamp;
            const isDefaultState = timestamp === 0;
            const isStale = !isDefaultState && timeDiff > IDLE_THRESHOLD;
            const portfolioSymbol = portfolio as PortfolioSymbolType;
            const isInArray = updatedPortfolios.includes(portfolioSymbol);
            if (isStale && !isInArray) {
              return [...updatedPortfolios, portfolioSymbol];
            } else if (!isStale && isInArray) {
              return updatedPortfolios.filter((p) => p !== portfolioSymbol);
            }
            return updatedPortfolios;
          },
          [...previous],
        ),
      );
    }, IDLE_THRESHOLD);

    return () => {
      clearInterval(interval);
    };
  }, [account, setStalePortfolios]);

  useEffect(() => {
    if (accountState && shouldFetchAccountState) {
      setAccountState((previous) => ({
        ...previous,
        ...Object.fromEntries(
          stalePortfolios.map((portfolio) => [
            portfolio,
            accountState[portfolio],
          ]),
        ),
      }));

      if (positions && shouldFetchAccountState) {
        const filteredPortfolios = stalePortfolios.filter(
          (portfolio) => portfolio !== PortfolioSymbol.USDC,
        );

        setPositions((previous) => ({
          ...previous,
          ...Object.fromEntries(
            filteredPortfolios.map((portfolio) => [
              Pairs[portfolio],
              positions[Pairs[portfolio]],
            ]),
          ),
        }));
      }
    }
  }, [
    positions,
    accountState,
    stalePortfolios,
    shouldFetchAccountState,
    setPositions,
    setAccountState,
  ]);

  // @dev using EoA account only for now;
  useEffect(() => {
    if (primaryWallet) {
      setEoa(primaryWallet.address as Address);
      setAccount(primaryWallet.address as Address);
    } else {
      setEoa(undefined);
      setAccount(undefined);

      if (!mirrorAccount) {
        setAccountState(defaultAccountState);
        setStalePortfolios([]);
        setAccountStateTimestamps(defaultAccountStateTimestamps);
      }
    }
  }, [
    accountState, // to trigger when accountState changes even after wallet disconnect
    primaryWallet,
    mirrorAccount,
    accountStateTimestamps, // to trigger when timestamps change even after wallet disconnect
    setEoa,
    setAccount,
    setAccountState,
    setStalePortfolios,
    setAccountStateTimestamps,
    setMirrorAccount,
  ]);

  useEffect(() => {
    if (account?.toLowerCase() !== serverAccount?.toLowerCase()) {
      setServerAccount(account).then(() => refetchServerAccount());
    }
  }, [serverAccount, account, refetchServerAccount]);

  return null;
};
